/**
 * /intl-breeders/:id — Public foreign-breeder kennel detail + US-buyer
 * inquiry form.
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeftIcon as ArrowLeft, GlobeHemisphereWestIcon as GlobeHemisphereWest, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

export default function IntlBreederDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [breedInterest, setBreedInterest] = useState("");
  const [notes, setNotes] = useState("");
  const [venueAck, setVenueAck] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/intl-breeders/${id}`).then((r) => setRow(r.data)).catch(() => setRow(null)).finally(() => setLoading(false));
  }, [id]);

  const submit = async () => {
    if (!venueAck) { toast.error("Please acknowledge the venue-only terms."); return; }
    setSubmitting(true);
    try {
      await api.post(`/intl-breeders/${id}/inquire`, {
        buyer_name: name.trim(),
        buyer_email: email.trim().toLowerCase(),
        buyer_phone: phone.trim(),
        breed_interest: breedInterest.trim(),
        notes: notes.trim(),
        venue_acknowledged: true,
      });
      setSent(true);
      toast.success("Inquiry sent — the breeder will reply directly to your email.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = user ? AppShell : PublicShell;
  if (loading) {
    return <Shell><div className="max-w-3xl mx-auto p-10 text-sm text-[#6B6B6B]">Loading…</div></Shell>;
  }
  if (!row) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto p-10 text-center" data-testid="intl-breeder-not-found">
          <p className="font-spectral text-2xl mb-2">Listing not found</p>
          <Link to="/intl-breeders" className="text-crimson underline">Back to directory</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <SeoHead
        title={`${row.kennel_name} · ${row.country} · Armstrong K9 Registry`}
        description={`${row.kennel_name} — international breeder listing dogs for US buyers via Armstrong K9 Registry's email-relay venue.`}
        url={`${webOrigin()}/intl-breeders/${id}`}
        type="article"
      />
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="intl-breeder-detail">
        <Link to="/intl-breeders" className="inline-flex items-center gap-1 text-[12px] text-[#6B6B6B] hover:text-crimson mb-6">
          <ArrowLeft size={12} weight="bold"/> Back to directory
        </Link>

        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">International Kennel</p>
            <h1 className="font-spectral text-3xl tracking-tight leading-tight">{row.kennel_name}</h1>
            <p className="text-[13px] text-[#6B6B6B] mt-1 inline-flex items-center gap-1.5">
              <GlobeHemisphereWest size={13} weight="duotone"/>
              {row.country}{row.city ? ` · ${row.city}` : ""}
              {row.years_breeding ? ` · ${row.years_breeding} yrs breeding` : ""}
            </p>
          </div>
          {row.website && (
            <a
              href={row.website.startsWith("http") ? row.website : `https://${row.website}`}
              target="_blank" rel="noopener noreferrer"
              className="text-[12px] text-crimson underline font-bold whitespace-nowrap"
              data-testid="intl-breeder-website-link"
            >
              Visit website ↗
            </a>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {row.intl_shipping_supported && (
            <span className="bg-[#EFF7EE] border border-[#0F8043]/40 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#0F8043]">Ships intl</span>
          )}
          {row.breeding_rights_offered && (
            <span className="bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]">Breeding rights</span>
          )}
        </div>

        {row.breeds?.length > 0 && (
          <p className="mt-4 text-[13.5px]">
            <span className="font-semibold">Breeds:</span> {row.breeds.join(", ")}
          </p>
        )}
        {row.bio && (
          <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-4 mt-4 text-[13.5px] leading-relaxed whitespace-pre-wrap" data-testid="intl-breeder-bio-block">
            {row.bio}
          </div>
        )}

        <div className="mt-8 rounded-xl border border-[#E8E0CC] bg-cream p-4 text-[12.5px] text-[#6B6B6B] leading-relaxed">
          <strong className="text-ink">Venue notice:</strong> Armstrong K9 Registry
          hosts the directory and relays your inquiry by email; we do not screen
          the kennel, verify the pedigree, hold any portion of the price, or
          coordinate international transport. Use written contracts and reputable
          couriers — browse{" "}
          <Link to="/transport/international" className="underline text-crimson">verified intl couriers</Link>.
        </div>

        <div className="mt-8 border-t border-[#EAEAEA] pt-8">
          {sent ? (
            <div className="rounded-xl border border-[#0F8043]/40 bg-[#EFF7EE] p-4 flex items-start gap-3" data-testid="intl-breeder-inquiry-sent">
              <CheckCircle size={20} weight="fill" className="text-[#0F8043] flex-shrink-0"/>
              <div className="text-[13px] leading-relaxed">
                <p className="font-semibold text-[#0F8043]">Inquiry sent.</p>
                <p className="text-[#3F3F3F] mt-0.5">
                  The breeder has been notified. They will reply to <strong>{email}</strong> directly.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-2">US Buyer Inquiry</p>
              <h2 className="font-spectral text-2xl tracking-tight leading-tight">Contact the kennel</h2>
              <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-2xl leading-relaxed">
                Free for buyers — the kennel pays the directory listing fee.
                We forward your message by email; replies land directly in your
                inbox.
              </p>
              <div className="mt-5 space-y-3 max-w-xl">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Your name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="intl-buyer-name"/>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="intl-buyer-email"/>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Phone (optional)</Label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" data-testid="intl-buyer-phone"/>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Breed interest</Label>
                    <Input value={breedInterest} onChange={(e) => setBreedInterest(e.target.value)} className="mt-1.5" data-testid="intl-buyer-breed"/>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Message</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 min-h-[120px]" maxLength={4000} placeholder="What you're looking for, timeline, intended use (working / show / pet), transport plan..." data-testid="intl-buyer-notes"/>
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-[12px] leading-relaxed bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
                  <input type="checkbox" checked={venueAck} onChange={(e) => setVenueAck(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="intl-buyer-venue-ack"/>
                  <span>
                    <strong>Venue acknowledgement.</strong> I understand Armstrong K9
                    Registry does not screen this kennel, verify the pedigree, or
                    coordinate transport. Any agreement is between me and the
                    kennel — Armstrong is not a party to it.
                  </span>
                </label>
                <Button
                  onClick={submit}
                  disabled={submitting || !name.trim() || !email.trim() || !notes.trim() || !venueAck}
                  className="rounded-full bg-ink hover:bg-crimson text-white"
                  data-testid="intl-buyer-submit"
                >
                  {submitting ? "Sending…" : "Send inquiry"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}
